import React, { useState, useRef, Children, useCallback, isValidElement, cloneElement, useEffect } from 'react';
import CarouselStyled from './bundle.esm2.js';
import Controls from './Controls/bundle.esm.js';
import Pagination from './Pagination/bundle.esm.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Carousel = (props) => {
  const { className, children, pagination, controls, scrollSpeed = 3 } = props;
  const { animated, infinite, onChange, prevIcon, nextIcon } = props;
  const { initialSlide, draggable } = props;
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isMoving, setIsMoving] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const carouselRef = useRef(null);
  const numberOfSlides = useRef(Children.count(children));
  const animationTimeout = useRef();
  const movingTimeout = useRef();
  const activeClass = isDown ? "Carousel__active" : "";
  const snapTo = useCallback(
    (slide) => {
      const carousel = carouselRef.current;
      if (!carousel)
        return;
      const { width: carouselWidth } = carousel.getBoundingClientRect();
      carousel.scrollTo({
        left: carouselWidth * (slide - 1),
        behavior: "smooth"
      });
      setCurrentSlide(slide);
      onChange == null ? void 0 : onChange(slide);
    },
    [onChange]
  );
  const pressStart = (pageX) => {
    const carousel = carouselRef.current;
    if (!carousel || !draggable)
      return;
    setIsDown(true);
    setStartX(pageX - carousel.offsetLeft);
    setScrollLeft(carousel.scrollLeft);
  };
  const animate = useCallback(() => {
    const isLastSlide = currentSlide === numberOfSlides.current;
    if (isLastSlide && !infinite)
      return;
    const newSlide = isLastSlide ? 1 : currentSlide + 1;
    snapTo(newSlide);
  }, [currentSlide, infinite, snapTo]);
  const startAnimation = useCallback(() => {
    const isNotNumber = typeof animated !== "number";
    if (!animated || isNotNumber || animated < 0 || isMoving || hasEntered)
      return;
    if (animationTimeout.current)
      clearTimeout(animationTimeout.current);
    animationTimeout.current = setTimeout(animate, animated);
  }, [animate, animated, hasEntered, isMoving]);
  const suspendAnimation = () => {
    if (!animationTimeout.current)
      return;
    clearTimeout(animationTimeout.current);
    animationTimeout.current = void 0;
  };
  const motionHandler = useCallback(
    (pageX) => {
      const carousel = carouselRef.current;
      if (!isDown || !carousel)
        return;
      setIsMoving(true);
      const x = pageX - carousel.offsetLeft;
      const walk = (x - startX) * scrollSpeed;
      carousel.scrollLeft = scrollLeft - walk;
    },
    [isDown, scrollLeft, scrollSpeed, startX]
  );
  const endMotionHandler = () => {
    const carousel = carouselRef.current;
    if (!isDown || !carousel)
      return;
    const { width: carouselWidth } = carousel.getBoundingClientRect();
    setCurrentSlide((prev) => {
      if (carousel.scrollLeft >= carouselWidth * (prev - 1 / 2)) {
        return prev + 1;
      }
      if (carousel.scrollLeft <= carouselWidth * (prev - 3 / 2)) {
        return prev - 1;
      }
      return prev;
    });
    if (movingTimeout.current)
      clearTimeout(movingTimeout.current);
    if (!infinite) {
      setIsDown(false);
      movingTimeout.current = setTimeout(() => setIsMoving(false), 100);
      return;
    }
    if (!isMoving) {
      setIsDown(false);
      return;
    }
    if (carousel.scrollLeft === carouselWidth * (numberOfSlides.current - 1)) {
      snapTo(1);
    }
    if (carousel.scrollLeft === 0) {
      snapTo(numberOfSlides.current);
    }
    setIsDown(false);
    movingTimeout.current = setTimeout(() => setIsMoving(false), 100);
  };
  const mouseDownHandler = (e) => {
    pressStart(e.pageX);
  };
  const touchStartHandler = (e) => {
    pressStart(e.changedTouches.item(0).pageX);
  };
  const mouseLeaveHandler = () => {
    endMotionHandler();
    setHasEntered(false);
    startAnimation();
  };
  const mouseUpHandler = (e) => {
    e.preventDefault();
    endMotionHandler();
  };
  const touchEndHandler = () => {
    endMotionHandler();
    startAnimation();
  };
  const mouseMoveHandler = useCallback(
    (e) => {
      e.preventDefault();
      motionHandler(e.pageX);
    },
    [motionHandler]
  );
  const touchMoveHandler = useCallback(
    (e) => {
      e.preventDefault();
      motionHandler(e.changedTouches.item(0).pageX);
    },
    [motionHandler]
  );
  const mouseEnterHandler = useCallback((e) => {
    setHasEntered(true);
    suspendAnimation();
  }, []);
  const clickCaptureHandler = useCallback(
    (e) => {
      if (!isMoving)
        return;
      e.preventDefault();
      e.stopPropagation();
    },
    [isMoving]
  );
  const renderChildren = useCallback(() => {
    var _a;
    const newChildren = Children.map(children, (child) => {
      if (!isValidElement(child))
        return;
      const { props: childProps } = child;
      const _a2 = childProps, { className: childClassName } = _a2, rest = __objRest(_a2, ["className"]);
      return /* @__PURE__ */ React.createElement("li", {
        className: "Carousel__item",
        onClickCapture: clickCaptureHandler
      }, cloneElement(child, __spreadProps(__spreadValues({}, rest), {
        className: `Carousel__inner-item ${childClassName ? childClassName : ""}`
      })));
    });
    numberOfSlides.current = (_a = newChildren == null ? void 0 : newChildren.length) != null ? _a : Children.count(children);
    return newChildren;
  }, [children, clickCaptureHandler]);
  useEffect(() => {
    startAnimation();
    return () => {
      if (!animationTimeout.current)
        return;
      clearTimeout(animationTimeout.current);
    };
  }, [startAnimation]);
  useEffect(() => {
    if (!initialSlide || typeof initialSlide !== "number")
      return;
    let slide = initialSlide;
    if (initialSlide > numberOfSlides.current)
      slide = numberOfSlides.current;
    if (initialSlide < 0)
      slide = 1;
    snapTo(slide);
    return () => {
      if (!movingTimeout.current)
        return;
      clearTimeout(movingTimeout.current);
    };
  }, [initialSlide, snapTo]);
  if (!children)
    return null;
  if (numberOfSlides.current === 0)
    return null;
  return /* @__PURE__ */ React.createElement(CarouselStyled, {
    className: `Carousel ${className}`,
    numberOfSlides: numberOfSlides.current
  }, /* @__PURE__ */ React.createElement("ul", {
    className: `Carousel__gallery ${activeClass}`,
    ref: carouselRef,
    onTouchStart: touchStartHandler,
    onTouchEnd: touchEndHandler,
    onTouchMove: touchMoveHandler,
    onMouseDown: mouseDownHandler,
    onMouseLeave: mouseLeaveHandler,
    onMouseUp: mouseUpHandler,
    onMouseMove: mouseMoveHandler,
    onMouseEnter: mouseEnterHandler
  }, renderChildren()), controls ? /* @__PURE__ */ React.createElement(Controls, {
    infinite,
    numberOfSlides: numberOfSlides.current,
    currentSlide,
    snapTo,
    prevIcon,
    nextIcon
  }) : null, pagination ? /* @__PURE__ */ React.createElement(Pagination, {
    numberOfSlides: numberOfSlides.current,
    currentSlide,
    snapTo
  }) : null);
};
Carousel.defaultProps = {
  className: "",
  controls: true,
  pagination: true,
  scrollSpeed: 3,
  infinite: false,
  draggable: true
};

export { Carousel as default };
//# sourceMappingURL=bundle.esm.js.map
